import { ref, watch, type VNode, isVNode, isRef, onBeforeUnmount } from 'vue'
import { isHTMLElement } from '../is'

export type UseElementRefOptions = {
  beforeDestroy?: (el: HTMLElement) => void | Promise<void>
}

export const useElementRef = (
  callback: (el: HTMLElement) => void | Promise<void>,
  options: UseElementRefOptions = {}
) => {
  const elref = ref<VNode | Element | undefined>(undefined)
  const rect = ref<DOMRect | undefined>(undefined)
  const htmlElement = ref<HTMLElement | undefined>(undefined)

  const getElement = (
    elem: typeof elref | VNode | Element | undefined
  ): HTMLElement | undefined => {
    if (!elem) return undefined
    if (isRef(elem)) return getElement(elem.value)
    const el = isVNode(elem) ? elem.el : elem
    return el as HTMLElement
  }

  const stop = watch(
    elref,
    async (elem) => {
      if (!elem) return
      const el = getElement(elem)

      if (el) {
        if (isHTMLElement(el)) {
          rect.value = el.getBoundingClientRect()
        } else if ((el as any).$el) {
          const e = (el as any).$el as HTMLElement
          if (isHTMLElement(e)) {
            rect.value = e.getBoundingClientRect()
          }
        }
      }

      if (el) {
        htmlElement.value = el
        callback(el)
      }
    },
    { immediate: true, deep: true }
  )

  onBeforeUnmount(async () => {
    if (options.beforeDestroy) {
      const el = getElement(elref)
      if (el) {
        console.log('beforeDestroy()')
        await options.beforeDestroy(el)
      }
    }
    stop()
  })

  return { ref: elref, rect, el: htmlElement }
}
